<template>
  <div class="dynamic-form-input__svg-choice-container">
    <label
      :class="{ 'error--text': isInvalid }"
      v-html="addNbsp(getLocaleText(inputData.label))"
    ></label>
    <label
      v-if="inputData.help"
      :class="{ 'error--text': isInvalid }"
      v-html="addNbsp(getLocaleText(inputData.help))"
    ></label>
    <div id="svg-root" :class="[`svg-root_${inputData.id}`, aspectRatioClass]">
      <svg width="100%" height="100%" v-html="inputData.placeholderFileUrl"></svg>
    </div>
    <Dialog
      :value="dialog"
      persistent
      :title="
        inputData.values.find(value => value.id === currentSelectedValueId)
          ? getLocaleText(inputData.values.find(value => value.id === currentSelectedValueId).value)
          : null
      "
      :buttons="buttons"
    >
      <template #text>
        <DynamicFormInput
          v-for="(depInput, index) in currentDependentInputs"
          :key="index"
          :class="{ 'pt-4': index > 0 }"
          :store="store"
          :input-data="depInput"
          :form-name="formName"
          :parent-value-id="currentSelectedValueId"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import formGtm from '@/mixins/formGtm';
import dynamicForms from '@/mixins/dynamicForms';

export default {
  name: 'UlSvgInput',
  mixins: [formGtm, dynamicForms],
  props: {
    inputData: {
      type: Object,
      required: true
    },
    store: {
      type: String,
      required: true
    },
    formName: {
      type: String,
      required: true
    },
    stepId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      inputValue: null,
      imageLoading: true,
      currentSelectedValueId: null,
      isInvalid: undefined,
      dialog: false,
      currentDependentInputs: []
    };
  },
  computed: {
    responses() {
      return this.$store.getters[`dynamicForms/responses`];
    },
    inputs() {
      return this.$store.getters[`dynamicForms/inputs`];
    },
    validations() {
      return this.$store.getters[`dynamicForms/validations`];
    },
    valueMapper() {
      return this.inputData.values.reduce((accumulator, currentValue) => {
        if (currentValue.htmlClass in accumulator) {
          return { ...accumulator };
        }
        return { ...accumulator, [currentValue.htmlClass]: currentValue.id };
      }, {});
    },
    aspectRatioClass() {
      const container = document.createElement('div');
      container.innerHTML = this.inputData.placeholderFileUrl;
      const [, , width, height] = container.firstChild.attributes.viewBox.nodeValue.split(' ');
      const aspectRatio = width / height;

      if (aspectRatio < 3 / 4) {
        return 'portrait';
      }

      if (aspectRatio > 4 / 3) {
        return 'landscape';
      }

      return 'square';
    },
    buttons() {
      return [
        {
          props: {
            color: 'tetrary'
          },
          events: {
            click: () => {
              this.closeSvgModal();
            }
          },
          text: this.$t('close')
        },
        {
          events: {
            click: () => {
              this.saveSvgModal();
            }
          },
          text: this.$t('save')
        }
      ];
    }
  },
  created() {
    this.inputValue = this.responses[this.formName][this.inputData.id] || [];

    this.$eventHub.$on(`validateInput_${this.inputData.id}_${this.stepId}`, () => {
      this.updateValue();
    });
  },
  async mounted() {
    this.$nextTick(() => {
      for (let value of this.inputData.values) {
        const elements = [
          ...document.querySelectorAll(
            `.svg-root_${this.inputData.id} g#clickable .${value.htmlClass}`
          )
        ];
        for (let element of elements) {
          element.addEventListener('mouseover', () => {
            elements.map(el => el.classList.add('hover'));
          });

          element.addEventListener('mouseout', () => {
            elements.map(el => el.classList.remove('hover'));
          });

          element.addEventListener('click', () => {
            this.processClick(elements, value);
          });
        }
      }

      // zprocesuj iniciální hodnoty
      if (this.inputValue && this.inputValue.length) {
        for (const inputValue of this.inputValue) {
          const { htmlClass } = this.inputData.values.find(val => val.id === inputValue);
          document
            .querySelectorAll(`.svg-root_${this.inputData.id} g#clickable .${htmlClass}`)
            .forEach(element => {
              element.classList.add('selected');
            });
        }
      }
    });
  },
  destroyed() {
    this.$eventHub.$off(`validateInput_${this.inputData.id}_${this.stepId}`);
  },
  methods: {
    processClick(applicableElements, dynamicInputValue) {
      let shouldBeAdded = false;
      let shouldBeRemoved = false;
      for (const el of applicableElements) {
        if (el.classList.contains('selected')) {
          el.classList.remove('selected');
          shouldBeRemoved = true;
        } else {
          el.classList.add('selected');
          shouldBeAdded = true;
        }
      }

      if (shouldBeAdded) {
        if (!this.inputValue) {
          this.inputValue = [dynamicInputValue.id];
        } else {
          this.inputValue = [...this.inputValue, dynamicInputValue.id];
        }
      }

      if (shouldBeRemoved) {
        this.inputValue = [...this.inputValue].filter(value => value !== dynamicInputValue.id);
      }

      this.updateValue();
      this.currentSelectedValueId = dynamicInputValue.id;
      this.currentDependentInputs = this.dependentInputs(
        this.inputData,
        this.currentSelectedValueId,
        this.formName
      );

      // zpracuj dependency inputy
      if (this.currentDependentInputs.length) {
        // manipulace tříd je rychlejší, takže element, který má třídu selected, je v procesu výběru,
        // zatímco element, který ji nemá, už byl "odvybrán"
        if (applicableElements[0].classList.contains('selected')) {
          this.dialog = true;
        } else {
          for (const depInput of this.currentDependentInputs) {
            this.$store.commit(`dynamicForms/updateResponse`, {
              formName: this.formName,
              id: depInput.id,
              value: null
            });
          }
        }
      }
    },
    updateValue() {
      const isValid = this.inputData.required
        ? !!(this.inputValue && this.inputValue.length)
        : true;
      this.$store.commit('dynamicForms/updateValidation', {
        formName: this.formName,
        id: this.inputData.id,
        value: isValid
      });

      this.$nextTick(() => {
        this.isInvalid = isValid === false;
      });

      this.$store.dispatch('dynamicForms/updateResponse', {
        formName: this.formName,
        id: this.inputData.id,
        value: this.inputValue.length ? this.inputValue : null
      });
      this.$emit('update:value');
    },
    saveSvgModal() {
      let isValid = this.currentDependentInputs
        .map(input => {
          this.$eventHub.$emit(`validateInput_${input.id}_${this.stepId}`);
          return !(
            input.required &&
            (!this.validations[this.formName][input.id] ||
              this.responses[this.formName][input.id] === null ||
              this.responses[this.formName][input.id] === undefined ||
              this.validations[this.formName][input.id] === false)
          );
        })
        .every(valid => valid);

      if (isValid) {
        this.updateValue();
        this.dialog = false;
        this.currentSelectedValueId = null;
        this.currentDependentInputs = [];
      }
    },
    closeSvgModal() {
      const isAnyDepRequired = this.currentDependentInputs.some(input => input.required);

      if (isAnyDepRequired) {
        this.inputValue = [...this.inputValue].filter(
          value => value !== this.currentSelectedValueId
        );
        const currentSelectedValue = this.inputData.values.find(
          value => value.id === this.currentSelectedValueId
        );
        for (const el of document.querySelectorAll(
          `.svg-root_${this.inputData.id} g#clickable .${currentSelectedValue.htmlClass}`
        )) {
          el.classList.remove('selected');
        }
        this.updateValue();
      }

      this.dialog = false;
      this.currentSelectedValueId = null;
      this.currentDependentInputs = [];
    }
  }
};
</script>

<style scoped lang="scss">
#svg-root {
  &.landscape {
    height: 300px;
    @media #{map-get($display-breakpoints, 'md-and-up')} {
      height: 450px;
    }
  }

  &.portrait {
    height: 700px;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      height: 800px;
    }
  }

  ::v-deep {
    g {
      &#clickable {
        cursor: pointer;

        &.custom-colors {
          path,
          circle {
            opacity: 0.5;

            &.selected {
              opacity: 1;
            }

            &.hover {
              opacity: 0.75;
            }
          }
        }

        &:not(.custom-colors) {
          path,
          circle {
            fill: transparentize($primary, 0.95);
            stroke: $primary;

            &.selected {
              stroke: $error;
              fill: transparentize($error, 0.65);
            }

            &.hover {
              fill: transparentize($error, 0.85);
            }
          }
        }
      }
    }
  }
}
</style>
